import React, { useContext, useMemo, useState } from 'react';
import { DictionaryAttribute } from 'lib';
import { AttributeCrosswalk } from './dictionaryAttributeCrosswalk';

interface AttributeListContextProps {
    initAttributeList: (attributes: any[]) => void;
    setAttribute: (id: string, value: any) => void;
    addAttribute: (parent?: string) => void;
    deleteAttribute: (attribute: DictionaryAttribute) => void;
    setSelectedAttribute: (attributeId?: string) => void;
    attributes: DictionaryAttribute[];
    crosswalk: any;
    selectedAttribute: any;
}

const AttributeListContext = React.createContext<AttributeListContextProps>({
    initAttributeList: () => {},
    setAttribute: () => {},
    addAttribute: () => {},
    deleteAttribute: () => {},
    setSelectedAttribute: () => {},
    attributes: [],
    crosswalk: {},
    selectedAttribute: {},
});

const useAttributeList = (): AttributeListContextProps =>
    useContext(AttributeListContext);

const AttributeListContextProvider = ({ children }: any): React.JSX.Element => {
    const [crosswalk, setCrosswalk] = useState<AttributeCrosswalk>();
    const [attribList, setAttribList] = useState<any[]>([]);
    const [selectedAttrib, setSelectedAttrib] = useState<DictionaryAttribute>();

    const init = (attributes: DictionaryAttribute[]): void => {
        const cw = new AttributeCrosswalk(attributes || []);
        setCrosswalk(cw);
        setAttribList(attributes);
    };

    const setAttribute = (id: string, newValue: DictionaryAttribute): void => {
        crosswalk?.setItem(id, newValue);
    };
    const handleSelectAttribute = (attributeId?: string): void => {
        if (attributeId) {
            setSelectedAttrib(crosswalk?.getItem(attributeId));
        } else {
            setSelectedAttrib(undefined);
        }
    };

    const delAttributeById = (
        id: string,
        attribute: any,
        attribListParams: any[]
    ): string[] => {
        const attribIndexToDelete = attribListParams.findIndex(
            (u: any) => u.id === id
        );
        if (attribIndexToDelete >= 0) {
            attribListParams.splice(attribIndexToDelete, 1);
            crosswalk?.crosswalk.delete(id);
        }

        if (attribute?.nodeAttributes?.length) {
            attribute.nodeAttributes.forEach((i: any) => {
                delAttributeById(id, i, attribute?.nodeAttributes);
            });
        }

        return attribList;
    };

    const handleDeleteAttribute = (attribute: any): void => {
        let newAttribList: DictionaryAttribute[] = [...attribList];

        newAttribList.forEach((i: any) => {
            delAttributeById(attribute.id, i, newAttribList);
        });

        setAttribList(newAttribList);
    };

    const handleAddAttribute = (parentId?: string): void => {
        const newAttrib: DictionaryAttribute = {
            id: crypto.randomUUID(),
            name: 'newAttribute',
            datatype: 'STRING',
        };

        let newAttribList: DictionaryAttribute[] = [];

        if (parentId) {
            const parent = crosswalk?.getItem(parentId);
            if (!parent.nodeAttributes) {
                parent.nodeAttributes = [];
            }

            parent.nodeAttributes.push(newAttrib);
            newAttribList = attribList.map((i: DictionaryAttribute) => {
                if (i.id === parentId) {
                    i.attributes?.push(newAttrib);
                }
                return i;
            });
            setAttribList(newAttribList);
        } else {
            newAttribList = attribList.map((i: DictionaryAttribute) => {
                return i;
            });
            newAttribList.push(newAttrib);
            setAttribList(newAttribList);
        }

        // set the new attribute as selected to open the edit drawer by default.
        setSelectedAttrib(newAttrib);

        crosswalk?.crosswalk.set(newAttrib.id, newAttrib);
    };

    return (
        <AttributeListContext.Provider
            value={useMemo(
                () => ({
                    initAttributeList: (attributes: DictionaryAttribute[]) =>
                        init(attributes),
                    setAttribute: (id: string, newValue: DictionaryAttribute) =>
                        setAttribute(id, newValue),
                    setSelectedAttribute: (attributeId?: string) =>
                        handleSelectAttribute(attributeId),
                    addAttribute: (parentId?: string) =>
                        handleAddAttribute(parentId),
                    deleteAttribute: (attribute: DictionaryAttribute) =>
                        handleDeleteAttribute(attribute),
                    attributes: attribList,
                    selectedAttribute: selectedAttrib,
                    crosswalk: crosswalk,
                }),
                [selectedAttrib, attribList] // eslint-disable-line react-hooks/exhaustive-deps
            )}
        >
            {children}
        </AttributeListContext.Provider>
    );
};

export { AttributeListContextProvider, useAttributeList };
